'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { type JSX, useCallback, useEffect } from 'react';
import { Label, ProgressBar } from 'react-aria-components';
import { create } from 'zustand';
import JourneyContentPlaceholder from '~/components/Journey/JourneyContentPlaceholder';
import { usePartner } from '~/libs/partners';
import type { PartnerCookie } from '~/utils/partners/constants';
import styles from './PartnerLoader.module.css';
import { PartnerLoaderCoOpHolidays } from './PartnerLoaderCoOpHolidays';
import { PartnerLoaderFirstChoice } from './PartnerLoaderFirstChoice';
import { PartnerLoaderTheGuardianHolidays } from './PartnerLoaderTheGuardianHolidays';
const MAX_LOAD_TIME = 15000;
const MAX_PROGRESS = 100;
const partnerLoaderComponents: Record<NonNullable<PartnerCookie>, () => JSX.Element> = {
  'first-choice': PartnerLoaderFirstChoice,
  'co-op holidays': PartnerLoaderCoOpHolidays,
  'guardian-holidays': PartnerLoaderTheGuardianHolidays
};
type LoaderStore = {
  progress: number;
  startTime: number;
  isActive: boolean;
  isProgressBarVisible: boolean;
  actions: {
    setProgress: (progress: number) => void;
    incrementProgress: (increment: number) => void;
    completeProgressBar: () => void;
    resetStartTime: () => void;
    setIsActive: (value: boolean) => void;
    setIsProgressBarVisible: (value: boolean) => void;
  };
};
export const useLoaderStore = create<LoaderStore>(set => ({
  progress: 0,
  startTime: Date.now(),
  isActive: false,
  isProgressBarVisible: true,
  actions: {
    setProgress: (progress: number) => set({
      progress
    }),
    incrementProgress: (increment: number) => set(state => ({
      progress: Math.min(state.progress + increment, MAX_PROGRESS)
    })),
    completeProgressBar: () => set({
      progress: 100,
      isActive: false,
      isProgressBarVisible: true
    }),
    resetStartTime: () => set({
      startTime: Date.now()
    }),
    setIsActive: (value: boolean) => set({
      isActive: value
    }),
    setIsProgressBarVisible: (value: boolean) => set({
      isProgressBarVisible: value
    })
  }
}));
const useInteractiveLoader = () => {
  const $ = _c(8);
  const {
    completeProgressBar,
    resetStartTime,
    setProgress,
    setIsActive,
    setIsProgressBarVisible
  } = useLoaderStore(_temp);
  let t0;
  if ($[0] !== resetStartTime || $[1] !== setIsActive || $[2] !== setIsProgressBarVisible || $[3] !== setProgress) {
    t0 = options => {
      resetStartTime();
      setProgress(0);
      setIsProgressBarVisible(options?.showProgressBar ?? true);
      setIsActive(true);
    };
    $[0] = resetStartTime;
    $[1] = setIsActive;
    $[2] = setIsProgressBarVisible;
    $[3] = setProgress;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const startLoader = t0;
  let t1;
  if ($[5] !== completeProgressBar || $[6] !== startLoader) {
    t1 = {
      startLoader,
      completeProgressBar
    };
    $[5] = completeProgressBar;
    $[6] = startLoader;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
};
const PartnerLoader = t0 => {
  const $ = _c(58);
  const {
    forcePartner,
    interactive,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "partner" : t1;
  const t = useTranslations();
  const progress = useLoaderStore(_temp2);
  const startTime = useLoaderStore(_temp3);
  const {
    incrementProgress
  } = useLoaderStore(_temp4);
  let t2;
  if ($[0] !== incrementProgress || $[1] !== interactive || $[2] !== progress || $[3] !== startTime) {
    t2 = () => {
      if (!interactive) {
        return;
      }
      const updateProgress = () => {
        const elapsed = Date.now() - startTime;
        const time = elapsed / MAX_LOAD_TIME;
        const easeOutT = time * (2 - time);
        const increment = (1 - easeOutT) * 25;
        incrementProgress(increment);
      };
      const timeoutTime = () => {
        const elapsed_0 = Date.now() - startTime;
        const remaining = MAX_LOAD_TIME - elapsed_0;
        const remainingProgress = MAX_PROGRESS - progress;
        const timeout = remaining / remainingProgress * 10;
        return Math.max(100, Math.min(1500, timeout));
      };
      const timer = setTimeout(updateProgress, timeoutTime());
      return () => clearTimeout(timer);
    };
    $[0] = incrementProgress;
    $[1] = interactive;
    $[2] = progress;
    $[3] = startTime;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== progress || $[6] !== startTime) {
    t3 = [startTime, progress];
    $[5] = progress;
    $[6] = startTime;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useEffect(t2, t3);
  const partnerCookieOrUtmSource = usePartner();
  const partner = forcePartner ?? partnerCookieOrUtmSource;
  if (variant === "topBar") {
    let t4;
    if ($[8] !== t) {
      t4 = t("aria.trip_availability_check");
      $[8] = t;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] !== t) {
      t5 = t6 => {
        const {
          percentage
        } = t6;
        return <div data-testid="loading-indicator"><h2 className="sr-only">{t("common.loading")}</h2><div className={styles.topBarProgressContainer}><div className={styles.topBarProgress} style={{
              width: `${percentage}%`
            }} /></div></div>;
      };
      $[10] = t;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    let t6;
    if ($[12] !== progress || $[13] !== t4 || $[14] !== t5) {
      t6 = <ProgressBar aria-label={t4} value={progress} maxValue={100}>{t5}</ProgressBar>;
      $[12] = progress;
      $[13] = t4;
      $[14] = t5;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    return t6;
  }
  if (variant === "map-review-panel") {
    let t4;
    if ($[16] !== t) {
      t4 = t("aria.duration_update");
      $[16] = t;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    let t5;
    if ($[18] !== t) {
      t5 = t6 => {
        const {
          percentage: percentage_0
        } = t6;
        return <div data-testid="loading-indicator"><h2 className="sr-only">{t("common.loading")}</h2><div className={styles.mapReviewPanelProgressContainer}><div className={styles.mapReviewPanelProgress} style={{
              width: `${percentage_0}%`
            }} /></div></div>;
      };
      $[18] = t;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    let t6;
    if ($[20] !== progress || $[21] !== t4 || $[22] !== t5) {
      t6 = <ProgressBar aria-label={t4} value={progress} maxValue={100}>{t5}</ProgressBar>;
      $[20] = progress;
      $[21] = t4;
      $[22] = t5;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    return t6;
  }
  if (!partner) {
    let t4;
    if ($[24] !== t) {
      t4 = t("aria.trip_loading");
      $[24] = t;
      $[25] = t4;
    } else {
      t4 = $[25];
    }
    let t5;
    if ($[26] !== t) {
      t5 = t6 => {
        const {
          percentage: percentage_1
        } = t6;
        return <div data-testid="loading-indicator"><h2 className="sr-only">{t("common.loading")}</h2><div className={styles.topBarProgressContainer}><div className={styles.topBarProgress} style={{
              width: `${percentage_1}%`
            }} /></div></div>;
      };
      $[26] = t;
      $[27] = t5;
    } else {
      t5 = $[27];
    }
    let t6;
    if ($[28] !== progress || $[29] !== t4 || $[30] !== t5) {
      t6 = <ProgressBar aria-label={t4} value={progress} maxValue={100}>{t5}</ProgressBar>;
      $[28] = progress;
      $[29] = t4;
      $[30] = t5;
      $[31] = t6;
    } else {
      t6 = $[31];
    }
    return t6;
  }
  const PartnerComponent = partnerLoaderComponents[partner] ?? undefined;
  if (!PartnerComponent) {
    let t4;
    if ($[32] !== t) {
      t4 = t("aria.trip_loading");
      $[32] = t;
      $[33] = t4;
    } else {
      t4 = $[33];
    }
    let t5;
    if ($[34] !== t) {
      t5 = t6 => {
        const {
          percentage: percentage_2
        } = t6;
        return <div data-testid="loading-indicator"><h2 className="sr-only">{t("common.loading")}</h2><div className={styles.topBarProgressContainer}><div className={styles.topBarProgress} style={{
              width: `${percentage_2}%`
            }} /></div></div>;
      };
      $[34] = t;
      $[35] = t5;
    } else {
      t5 = $[35];
    }
    let t6;
    if ($[36] !== progress || $[37] !== t4 || $[38] !== t5) {
      t6 = <ProgressBar aria-label={t4} value={progress} maxValue={100}>{t5}</ProgressBar>;
      $[36] = progress;
      $[37] = t4;
      $[38] = t5;
      $[39] = t6;
    } else {
      t6 = $[39];
    }
    return t6;
  }
  let t4;
  if ($[40] !== PartnerComponent) {
    t4 = <PartnerComponent />;
    $[40] = PartnerComponent;
    $[41] = t4;
  } else {
    t4 = $[41];
  }
  let t5;
  if ($[42] !== interactive || $[43] !== t) {
    t5 = !interactive && <h2 className="mt-4 typography-title-5">{t("common.loading")}</h2>;
    $[42] = interactive;
    $[43] = t;
    $[44] = t5;
  } else {
    t5 = $[44];
  }
  let t6;
  if ($[45] !== t) {
    t6 = t("content.loading_routes_description");
    $[45] = t;
    $[46] = t6;
  } else {
    t6 = $[46];
  }
  let t7;
  if ($[47] !== t6) {
    t7 = <p className="max-w-sm mt-4">{t6}</p>;
    $[47] = t6;
    $[48] = t7;
  } else {
    t7 = $[48];
  }
  let t8;
  if ($[49] !== interactive || $[50] !== progress || $[51] !== t) {
    t8 = interactive && <ProgressBar value={progress} maxValue={100}>{t9 => {
        const {
          percentage: percentage_3,
          valueText
        } = t9;
        return <><div className={styles.labels}><Label>{t("common.loading")}</Label><span className="value">{valueText}</span></div><div className={styles.progressBarContainer}><div className={styles.progressBar} style={{
              width: `${percentage_3}%`
            }} /></div></>;
      }}</ProgressBar>;
    $[49] = interactive;
    $[50] = progress;
    $[51] = t;
    $[52] = t8;
  } else {
    t8 = $[52];
  }
  let t9;
  if ($[53] !== t4 || $[54] !== t5 || $[55] !== t7 || $[56] !== t8) {
    t9 = <JourneyContentPlaceholder><div className="absolute inset-x-0 z-10 flex justify-center top-4"><div className="p-6 pt-8 text-center bg-white shadow-lg max-w-[21rem] rounded-xl">{t4}{t5}{" "}{t7}{t8}</div></div></JourneyContentPlaceholder>;
    $[53] = t4;
    $[54] = t5;
    $[55] = t7;
    $[56] = t8;
    $[57] = t9;
  } else {
    t9 = $[57];
  }
  return t9;
};
export { PartnerLoader, useInteractiveLoader };
function _temp(state) {
  return state.actions;
}
function _temp2(state) {
  return state.progress;
}
function _temp3(state_0) {
  return state_0.startTime;
}
function _temp4(state_1) {
  return state_1.actions;
}